(() => {
  const config = window.CK_CONFIG || {}
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || config.root || "/"

  const DAY = 24 * 60 * 60 * 1000
  const TTL_MS = 30 * DAY
  const CARLA_KEY = "ck-carla-attribution"
  const CAMPAIGN_KEY = "ck-attribution"
  const UPSELL_KEY = "ck-upsell-current"
  const DISCOUNT_KEY = "ck-discount"

  const CAMPAIGN_PARAMS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_id",
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "ck_adset_id",
    "ck_ad_id",
    "ck_placement",
    "ck_source",
  ]

  const ATTRIBUTION_KEYS = [
    "carla_lead",
    "carla_keyword",
    ...CAMPAIGN_PARAMS,
    "ga_client_id",
    "ga_session_id",
    "_fbp",
    "fbc",
  ]

  const storage = {
    get(store, key) {
      try {
        return JSON.parse(store.getItem(key) || "null")
      } catch {
        return null
      }
    },
    set(store, key, value) {
      try {
        store.setItem(key, JSON.stringify(value))
      } catch {}
    },
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&")}=([^;]*)`))
    if (!match) return ""
    try {
      return decodeURIComponent(match[1])
    } catch {
      return match[1]
    }
  }

  const consent = {
    api() {
      return window.Shopify && window.Shopify.customerPrivacy
    },
    load() {
      return new Promise((resolve) => {
        if (consent.api()) return resolve(consent.api())
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== "function") return resolve(null)
        let settled = false
        const done = () => {
          if (settled) return
          settled = true
          resolve(consent.api() || null)
        }
        try {
          window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], done)
        } catch {
          done()
        }
        setTimeout(done, 1500)
      })
    },
    marketingAllowed() {
      try {
        const api = consent.api()
        return Boolean(api && api.marketingAllowed())
      } catch {
        return false
      }
    },
    analyticsAllowed() {
      try {
        const api = consent.api()
        return Boolean(api && api.analyticsProcessingAllowed())
      } catch {
        return false
      }
    },
    showPreferences() {
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === "function") {
        window.privacyBanner.showPreferences()
        return true
      }
      return false
    },
  }

  function captureCarla(query) {
    const lead = (query.get("carla_lead") || "").trim()
    if (!lead) return
    const keyword = (query.get("carla_keyword") || "").trim()
    storage.set(localStorage, CARLA_KEY, { lead, keyword, ts: Date.now() })
  }

  function readCarla() {
    const record = storage.get(localStorage, CARLA_KEY)
    if (!record || typeof record.lead !== "string" || !record.lead || typeof record.ts !== "number" || Date.now() - record.ts > TTL_MS) {
      return { lead: "", keyword: "" }
    }
    return { lead: record.lead, keyword: typeof record.keyword === "string" ? record.keyword : "" }
  }

  function readTouch() {
    const record = storage.get(localStorage, CAMPAIGN_KEY)
    const now = Date.now()
    if (
      !record ||
      typeof record.ts !== "number" ||
      !Number.isFinite(record.ts) ||
      record.ts > now ||
      now - record.ts > TTL_MS ||
      !record.params ||
      typeof record.params !== "object" ||
      Array.isArray(record.params)
    ) {
      return null
    }
    const params = {}
    for (const key of CAMPAIGN_PARAMS) {
      const value = record.params[key]
      if (typeof value === "string" && value.trim()) params[key] = value.trim()
    }
    if (!Object.keys(params).length) return null
    return {
      params,
      ts: record.ts,
      bot: record.bot === true || (params.utm_source || "").toLowerCase() === "ig_dm",
    }
  }

  function captureCampaign(query) {
    const params = {}
    for (const key of CAMPAIGN_PARAMS) {
      const value = (query.get(key) || "").trim()
      if (value) params[key] = value
    }
    if (!Object.keys(params).length) return
    const bot = Boolean((query.get("carla_lead") || "").trim()) || (params.utm_source || "").toLowerCase() === "ig_dm"
    try {
      const previousRecent = storage.get(sessionStorage, UPSELL_KEY)
      const recentBot = (params.utm_source || "").toLowerCase() === "ig_dm"
      const email = /^(email|e-mail)$/i.test(params.utm_medium || "") || /^(email|welcome|access_email)$/i.test(params.utm_source || "")
      if (!email && (!recentBot || !previousRecent || previousRecent.bot)) {
        storage.set(sessionStorage, UPSELL_KEY, { params, ts: Date.now(), bot: recentBot })
      }
    } catch {}
    const previous = readTouch()
    if (previous && (!previous.bot || bot)) return
    storage.set(localStorage, CAMPAIGN_KEY, { params, ts: Date.now(), bot })
  }

  function gaIdentifiers() {
    const result = {}
    const ga = readCookie("_ga")
    const clientMatch = ga.match(/^GA\d\.\d\.(\d+\.\d+)$/)
    if (clientMatch) result.ga_client_id = clientMatch[1]
    const measurement = (config.gaMeasurementId || "").replace(/^G-/, "")
    const sessionCookie = measurement ? readCookie(`_ga_${measurement}`) : ""
    const sessionMatch = sessionCookie.match(/^GS\d\.\d\.(?:s)?(\d+)/)
    if (sessionMatch) result.ga_session_id = sessionMatch[1]
    return result
  }

  function sanitize(input) {
    const result = {}
    for (const key of ATTRIBUTION_KEYS) {
      const value = input[key]
      if (typeof value !== "string") continue
      const clean = value.trim()
      const wellFormed = typeof clean.toWellFormed === "function" ? clean.toWellFormed() : clean
      if (!clean || clean !== wellFormed || /[\p{Cc}\p{Cf}�]/u.test(clean) || Array.from(clean).length > 500) continue
      if (key === "ga_client_id" && !/^\d+\.\d+$/.test(clean)) continue
      if (key === "ga_session_id" && !/^\d+$/.test(clean)) continue
      if ((key === "_fbp" || key === "fbc") && !/^fb\.\d+\.\d{13}\.[A-Za-z0-9_-]+$/.test(clean)) continue
      if (["gclid", "gbraid", "wbraid", "fbclid"].includes(key) && !/^[A-Za-z0-9_.~-]+$/.test(clean)) continue
      result[key] = clean
    }
    if (!result.carla_lead) delete result.carla_keyword
    return result
  }

  function collectAttributes() {
    const touch = readTouch()
    const carla = readCarla()
    const raw = { ...(touch ? touch.params : {}), carla_lead: carla.lead, carla_keyword: carla.keyword }
    if (consent.marketingAllowed()) {
      raw._fbp = readCookie("_fbp")
      raw.fbc = readCookie("_fbc") || (touch && touch.params.fbclid ? `fb.1.${touch.ts}.${touch.params.fbclid}` : "")
    }
    if (consent.analyticsAllowed()) Object.assign(raw, gaIdentifiers())
    const clean = sanitize(raw)
    const attributes = {}
    for (const key of ATTRIBUTION_KEYS) attributes[key] = clean[key] || ""
    return attributes
  }

  function captureAttribution() {
    try {
      const query = new URLSearchParams(window.location.search)
      captureCarla(query)
      captureCampaign(query)
    } catch {}
  }

  // Analytics: GA4 on every page behind Google Consent Mode (fed by Shopify's
  // cookie banner), as on the headless site, and the Meta Pixel on every page,
  // without consent gating unless the theme setting asks for it. Purchases are
  // sent by the store's customer event pixels, not from here.
  const GA_ID = /^G-[A-Z0-9]+$/i.test(config.gaMeasurementId || "") ? config.gaMeasurementId : ""
  const META_PIXEL_ID = /^\d+$/.test(config.metaPixelId || "") ? config.metaPixelId : ""
  const SCROLL_THRESHOLDS = [50, 75, 90]
  const gaQueue = []
  const scrollFired = new Set()
  let gaEnabled = false
  let gaConfigured = false
  let metaStarted = false

  function productAnalytics() {
    const node = document.querySelector("[data-product-analytics]")
    if (!node) return null
    try {
      const data = JSON.parse(node.textContent)
      return Array.isArray(data.items) && data.items.length ? data : null
    } catch {
      return null
    }
  }

  function trackEvent(name, params) {
    if (!gaEnabled) return
    if (gaConfigured) window.gtag("event", name, params)
    else gaQueue.push([name, params])
  }

  function applyGaConsent(detail) {
    const known = Boolean(detail) && typeof detail.analyticsAllowed === "boolean"
    const analytics = known ? detail.analyticsAllowed : consent.analyticsAllowed()
    const ads = (known ? detail.marketingAllowed : consent.marketingAllowed()) ? "granted" : "denied"
    window.gtag("consent", "update", {
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
      analytics_storage: analytics ? "granted" : "denied",
    })
  }

  function initGa() {
    if (!GA_ID) return
    gaEnabled = true
    window.dataLayer = window.dataLayer || []
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments)
      }
    // Denied until the cookie banner says otherwise (Loi 25 / GDPR), as on the site.
    window.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500,
    })
    window.gtag("js", new Date())
    const script = document.createElement("script")
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`
    document.head.append(script)
    document.addEventListener("visitorConsentCollected", (event) => applyGaConsent(event.detail))
    consent.load().then(() => {
      applyGaConsent()
      window.gtag("config", GA_ID)
      gaConfigured = true
      for (const [name, params] of gaQueue.splice(0)) window.gtag("event", name, params)
    })
  }

  function trackMeta(event, params) {
    if (metaStarted) window.fbq("track", event, params)
  }

  function metaPageView(product) {
    trackMeta("PageView")
    if (!product) return
    const item = product.items[0]
    trackMeta("ViewContent", {
      content_ids: [item.item_id],
      content_type: "product",
      content_name: item.item_name,
      value: item.price / 100,
      currency: product.currency,
      ...(product.category ? { content_category: product.category } : {}),
    })
  }

  function startMeta(product) {
    if (metaStarted) return
    if (!window.fbq) {
      // Meta's standard base code: queue calls until fbevents.js has loaded.
      const fbq = function () {
        if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments)
        else fbq.queue.push(arguments)
      }
      fbq.push = fbq
      fbq.loaded = true
      fbq.version = "2.0"
      fbq.queue = []
      window.fbq = fbq
      if (!window._fbq) window._fbq = fbq
      const script = document.createElement("script")
      script.async = true
      script.src = "https://connect.facebook.net/en_US/fbevents.js"
      document.head.append(script)
    }
    window.fbq("init", META_PIXEL_ID)
    metaStarted = true
    metaPageView(product)
  }

  function initMeta(product) {
    if (!META_PIXEL_ID) return
    if (!config.metaPixelConsent) {
      startMeta(product)
      return
    }
    consent.load().then(() => {
      if (consent.marketingAllowed()) {
        startMeta(product)
        return
      }
      document.addEventListener("visitorConsentCollected", (event) => {
        if (event.detail && event.detail.marketingAllowed) startMeta(product)
      })
    })
  }

  function trackViewItem(product) {
    if (!product) return
    const item = product.items[0]
    trackEvent("view_item", {
      currency: product.currency,
      value: item.price / 100,
      items: [
        {
          item_id: item.item_id,
          item_name: item.item_name,
          price: item.price / 100,
          quantity: 1,
          ...(product.category ? { item_category: product.category } : {}),
        },
      ],
    })
  }

  function trackBeginCheckout(lines) {
    const product = productAnalytics()
    if (!product) return
    const picked = lines.map((line) => product.items.find((item) => item.item_id === String(line.id))).filter(Boolean)
    if (!picked.length) return
    const value = picked.reduce((sum, item) => sum + item.price, 0) / 100
    const items = picked.map((item) => ({ item_id: item.item_id, item_name: item.item_name, price: item.price / 100, quantity: 1 }))
    trackEvent("begin_checkout", { currency: product.currency, value, items })
    trackMeta("InitiateCheckout", {
      content_ids: items.map((item) => item.item_id),
      content_type: "product",
      value,
      currency: product.currency,
      num_items: items.length,
    })
  }

  function trackLead(method, afterPost) {
    // A posted form keeps its success state on refresh and back/forward: count it once.
    if (afterPost) {
      try {
        const entry = performance.getEntriesByType("navigation")[0]
        if (entry && entry.type !== "navigate") return
      } catch {}
    }
    trackEvent("generate_lead", { method, locale: config.locale || "en" })
  }

  function trackClick(event) {
    const cta = event.target.closest("[data-cta-location]")
    if (cta) {
      trackEvent("click_cta", {
        cta_location: cta.dataset.ctaLocation,
        ...(cta.dataset.ctaText ? { cta_text: cta.dataset.ctaText } : {}),
        ...(cta.getAttribute("href") ? { link_url: cta.getAttribute("href") } : {}),
      })
    }
    const step = event.target.closest("[data-step-select]")
    if (step) {
      trackEvent("select_program_step", {
        step: Number(step.dataset.step) || 0,
        ...(step.dataset.programName ? { program_name: step.dataset.programName } : {}),
        ...(step.dataset.itemId ? { item_id: step.dataset.itemId } : {}),
        location: step.dataset.stepSelect,
      })
    }
  }

  function initScrollDepth() {
    let ticking = false
    const check = () => {
      ticking = false
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const percent = (window.scrollY / scrollable) * 100
      for (const threshold of SCROLL_THRESHOLDS) {
        if (percent >= threshold && !scrollFired.has(threshold)) {
          scrollFired.add(threshold)
          trackEvent("scroll_depth", { percent: threshold, page_path: window.location.pathname })
        }
      }
    }
    window.addEventListener(
      "scroll",
      () => {
        if (ticking || scrollFired.size === SCROLL_THRESHOLDS.length) return
        ticking = true
        requestAnimationFrame(check)
      },
      { passive: true }
    )
    check()
  }

  function initTracking() {
    if (editorMode()) return
    const product = productAnalytics()
    initGa()
    initMeta(product)
    trackViewItem(product)
    initScrollDepth()
    document.addEventListener("click", trackClick)
    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return
      // Back/forward cache: the page is shown again without reloading, count the visit.
      trackEvent("page_view", { page_location: window.location.href, page_title: document.title })
      trackViewItem(product)
      if (metaStarted) metaPageView(product)
      scrollFired.clear()
    })
  }

  async function postJSON(path, body) {
    const response = await fetch(root + path, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body || {}),
      credentials: "same-origin",
    })
    if (!response.ok) {
      const error = new Error(`${path} failed with ${response.status}`)
      error.status = response.status
      throw error
    }
    return response.json().catch(() => ({}))
  }

  function storedDiscount() {
    const record = storage.get(localStorage, DISCOUNT_KEY)
    if (!record || typeof record.code !== "string" || !record.code || Date.now() - (record.ts || 0) > 60 * DAY) return ""
    return record.code
  }

  function rememberDiscount(code) {
    if (code) storage.set(localStorage, DISCOUNT_KEY, { code, ts: Date.now() })
  }

  function submitToCheckout() {
    const form = document.createElement("form")
    form.method = "post"
    form.action = config.cartUrl || `${root}cart`
    form.hidden = true
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "checkout"
    input.value = "Checkout"
    form.append(input)
    document.body.append(form)
    form.submit()
  }

  let leavingPage = false
  window.addEventListener("pagehide", () => {
    leavingPage = true
  })

  function editorMode() {
    return Boolean(window.Shopify && window.Shopify.designMode)
  }

  async function buyNow(items) {
    const lines = (items || [])
      .map((item) => ({ id: Number(item.id), quantity: Math.max(1, Math.min(5, Number(item.quantity) || 1)) }))
      .filter((item) => Number.isSafeInteger(item.id) && item.id > 0)
      .slice(0, 5)
    if (!lines.length) throw new Error("No line items")
    await consent.load()
    const attributes = collectAttributes()
    await postJSON("cart/clear.js")
    await postJSON("cart/add.js", { items: lines })
    try {
      await postJSON("cart/update.js", { attributes })
    } catch {}
    const code = storedDiscount()
    if (code) {
      try {
        await fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`, {
          credentials: "same-origin",
        })
      } catch {}
    }
    document.dispatchEvent(new CustomEvent("ck:checkout", { detail: { items: lines } }))
    submitToCheckout()
    await new Promise((resolve) => setTimeout(resolve, 10000))
    if (!leavingPage) {
      const error = new Error("Checkout did not open")
      error.stalled = true
      throw error
    }
  }

  function setLoading(button, loading) {
    if (!button) return
    button.classList.toggle("is-loading", loading)
    button.toggleAttribute("disabled", loading)
    button.setAttribute("aria-busy", loading ? "true" : "false")
    const label = button.querySelector("[data-label]")
    if (label) {
      if (loading) {
        button.ckLabel = label.innerHTML
        label.textContent = config.strings?.processing || "…"
      } else if (button.ckLabel) {
        label.innerHTML = button.ckLabel
        button.ckLabel = null
      }
    }
  }

  async function handleBuy(button) {
    const form = button.closest("[data-buy-form]") || button
    const error = form.querySelector("[data-buy-error]") || document.querySelector(`[data-buy-error="${form.dataset.buyForm || ""}"]`)
    if (error) error.hidden = true
    let items
    try {
      items = JSON.parse(button.dataset.items || form.dataset.items || "[]")
    } catch {
      items = []
    }
    if (typeof form.ckItems === "function") items = form.ckItems()
    if (editorMode()) {
      if (error) {
        error.hidden = false
        error.textContent = config.strings?.editorNotice || ""
      }
      return
    }
    trackBeginCheckout(items)
    setLoading(button, true)
    try {
      await buyNow(items)
    } catch (err) {
      setLoading(button, false)
      if (error) {
        error.hidden = false
        error.textContent = config.strings?.checkoutError || "Error"
      }
    }
  }

  document.addEventListener("click", (event) => {
    const buy = event.target.closest("[data-buy-now]")
    if (buy) {
      event.preventDefault()
      handleBuy(buy)
      return
    }

    const opener = event.target.closest("[data-dialog-open]")
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen)
      if (dialog && typeof dialog.showModal === "function") {
        event.preventDefault()
        openDialog(dialog, opener)
      }
      return
    }

    const closer = event.target.closest("[data-dialog-close]")
    if (closer) {
      const dialog = closer.closest("dialog")
      if (dialog) dialog.close()
      return
    }

    if (event.target instanceof HTMLDialogElement && event.target.open && event.target.hasAttribute("data-light-dismiss")) {
      const rect = event.target.getBoundingClientRect()
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom
      if (!inside) event.target.close()
    }

    const prefs = event.target.closest("[data-cookie-preferences]")
    if (prefs && consent.showPreferences()) event.preventDefault()
  })

  function openDialog(dialog, opener) {
    dialog.ckOpener = opener || document.activeElement
    dialog.showModal()
    document.body.classList.add("is-locked")
    dialog.dispatchEvent(new CustomEvent("ck:open"))
  }

  document.addEventListener(
    "close",
    (event) => {
      const dialog = event.target
      if (!(dialog instanceof HTMLDialogElement)) return
      if (!document.querySelector("dialog[open]")) document.body.classList.remove("is-locked")
      const opener = dialog.ckOpener
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus({ preventScroll: true })
      dialog.ckOpener = null
    },
    true
  )

  function initReveal() {
    const items = document.querySelectorAll("[data-reveal]:not(.is-revealed)")
    if (!items.length) return
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      items.forEach((item) => item.classList.add("is-revealed"))
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          entry.target.classList.add("is-revealed")
          observer.unobserve(entry.target)
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    )
    items.forEach((item) => observer.observe(item))
  }

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) document.querySelectorAll("[data-buy-now].is-loading").forEach((button) => setLoading(button, false))
  })

  captureAttribution()
  initTracking()

  window.CK = Object.assign(window.CK || {}, {
    config,
    consent,
    attribution: { capture: captureAttribution, collect: collectAttributes, sanitize, keys: ATTRIBUTION_KEYS },
    track: { event: trackEvent, lead: trackLead },
    checkout: { buyNow, rememberDiscount, storedDiscount },
    dialogs: { open: openDialog },
    reveal: initReveal,
  })

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initReveal)
  else initReveal()
  document.addEventListener("shopify:section:load", initReveal)
})()
